'use client';
import { Button } from '@curology/ui-components-web/react/Button';
import { CheckmarkIcon } from '@curology/ui-components-web/react/Icon/icons/CheckmarkIcon';
import { CrossIcon } from '@curology/ui-components-web/react/Icon/icons/CrossIcon';
import { MaskedInput } from '@curology/ui-components-web/react/MaskedInput';
import { Modal } from '@curology/ui-components-web/react/Modal';
import { ModalContent } from '@curology/ui-components-web/react/Modal/ModalContent';
import { TextInput } from '@curology/ui-components-web/react/TextInput';
import { Typography } from '@curology/ui-components-web/react/Typography';
import { useAtom } from 'jotai';
import React, { useEffect, useState } from 'react';

import { FlashMessagesState } from '@/atoms/flashMessages/flashMessagesState';
import { type Brand } from '@/consts/brands';
import { PhoneNumberVerificationService } from '@/services/phoneNumberVerificationService/client';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

const VIEWS = {
  phoneNumberEntry: 'phoneNumberEntry',
  verificationCodeEntry: 'verificationCodeEntry',
};

export const VerifyPhoneNumberModal = ({
  brand,
  phoneNumber,
  setPhoneNumber,
  setShowVerifyPhoneNumberModal,
  showVerifyPhoneNumberModal,
}: {
  brand: Brand;
  phoneNumber: string;
  setPhoneNumber: React.Dispatch<React.SetStateAction<string>>;
  setShowVerifyPhoneNumberModal: React.Dispatch<React.SetStateAction<boolean>>;
  showVerifyPhoneNumberModal: boolean;
}) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [view, setView] = useState(VIEWS.phoneNumberEntry);

  const [flashMessages, setFlashMessages] = useAtom(
    FlashMessagesState.flashMessagesAtom,
  );

  useEffect(() => {
    if (showVerifyPhoneNumberModal && view === VIEWS.phoneNumberEntry) {
      trackEvent(AnalyticsEvent.PHONE_NUMBER_CONFIRMATION_STARTED);
    }
  }, [showVerifyPhoneNumberModal, view]);

  return (
    <Modal
      onClose={() => setShowVerifyPhoneNumberModal(false)}
      open={showVerifyPhoneNumberModal}
      type="light"
      width={423}
    >
      <ModalContent className="cur-max-w-[390px]">
        {view === VIEWS.phoneNumberEntry && (
          <>
            <Typography className="cur-mb-4" size="h1">
              What’s your phone number?
            </Typography>
            <Typography className="cur-my-4">
              We’ll text you a one-time verification code to make sure you’re
              really you.
            </Typography>
            <form
              className="cur-bg-surface-neutral cur-mt-4 cur-p-4"
              onSubmit={async (event) => {
                event.preventDefault();

                await PhoneNumberVerificationService.postPhoneNumberToTextConfirmationCode(
                  brand,
                  phoneNumber,
                );

                setView(VIEWS.verificationCodeEntry);
              }}
            >
              <div className="cur-mb-4">
                <MaskedInput
                  label="Phone number"
                  mask={[
                    /[0-9]/,
                    /[0-9]/,
                    /[0-9]/,
                    '-',
                    /[0-9]/,
                    /[0-9]/,
                    /[0-9]/,
                    '-',
                    /[0-9]/,
                    /[0-9]/,
                    /[0-9]/,
                    /[0-9]/,
                  ]}
                  onChange={(event) => setPhoneNumber(event.target.value)}
                  placeholder="123-456-7890"
                  required
                  value={phoneNumber}
                />
              </div>
              <div className="cur-mt-4">
                <Button className="cur-w-full" type="submit">
                  Send code
                </Button>
              </div>
            </form>
          </>
        )}
        {view === VIEWS.verificationCodeEntry && (
          <>
            <Typography className="cur-mb-4" size="h1">
              Verify your phone number
            </Typography>
            <Typography className="cur-my-4">
              We just texted you! Enter the code you received below.
            </Typography>
            <form
              className="cur-bg-surface-neutral cur-mt-4 cur-p-4"
              onSubmit={async (event) => {
                event.preventDefault();

                trackEvent(
                  AnalyticsEvent.EXPRESS_CHECKOUT_PHONE_CONFIRMATION_CODE_ENTERED,
                );

                try {
                  const { data: phoneNumberObject } =
                    await PhoneNumberVerificationService.postConfirmationCode(
                      brand,
                      verificationCode,
                    );

                  if (phoneNumberObject.confirmedAt) {
                    setShowVerifyPhoneNumberModal(false);

                    await PhoneNumberVerificationService.postSmsSendMessage(
                      brand,
                    );

                    setFlashMessages([
                      ...flashMessages,
                      {
                        children:
                          'We just texted you a link to upload your photos.',
                        icon: <CheckmarkIcon />,
                        iconSize: 'sm',
                        state: 'success',
                        title: 'Great!',
                      },
                    ]);

                    trackEvent(AnalyticsEvent.PHOTO_UPLOAD_SMS_LINK_SENT);
                  }
                } catch (exception) {
                  setFlashMessages([
                    ...flashMessages,
                    {
                      children: 'Incorrect code. Please try again.',
                      icon: <CrossIcon />,
                      iconSize: 'sm',
                      state: 'critical',
                      title: 'Sorry!',
                    },
                  ]);
                }
              }}
            >
              <div className="cur-mb-4">
                <TextInput
                  label="Verification code"
                  onChange={(event) => setVerificationCode(event.target.value)}
                  placeholder="Code"
                  required
                  value={verificationCode}
                />
              </div>
              <div className="cur-my-4">
                <Button className="cur-w-full" type="submit">
                  Verify
                </Button>
              </div>
              <div className="cur-mt-4">
                <Button
                  buttonType="text"
                  className="cur-w-full"
                  onClick={() => setView(VIEWS.phoneNumberEntry)}
                  state="dark"
                  type="submit"
                >
                  Back
                </Button>
              </div>
            </form>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
